import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { useHealthToolsCache } from '@hhgtech/hhg-components/hooks'
import { Button, LoadingOverlay, Text } from '@hhgtech/hhg-components/mantine'
import { theme } from '@hhgtech/hhg-components/misc'
import { Box, Container, Flex, Space } from '@mantine/core'
import cookieParser from 'cookie'
import dayjs from 'dayjs'
import flatten from 'lodash/flatten'
import queryString from 'query-string'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { formatTogetherTopic, formatUserProfileUrl } from 'api/dataTransform'
import { formatVideoItem } from 'api/dataTransform/videosPage'
import { PATHS } from 'api/paths'
import { getApiPath, getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/fixedBottomAdBlock'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { PregnancyNavigation } from 'components/molecules/pregnancyNavigation'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { DueDateCalculatorCard } from 'components/organisms/dueDateCalculatorCard'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { StyledDueDateWrapper } from 'components/organisms/pregnancyTimeline/index.styled'
import { SharePopupNew } from 'components/organisms/sharePopupNew'
import { OutOfPageAd } from 'components/pages/article/components/OutOfPageAd'
import {
  DueDateCalculator,
  DueDateCalculatorType,
} from 'components/templates/dueDateCalculator'
import {
  getDueDateConception,
  getDueDateIUI,
  getDueDateIVF,
  getDueDateLastPeriod,
  getDueDateUltrasound,
  STORE_KEY,
  useDueDateStore,
} from 'components/templates/dueDateCalculator/utils'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import useFetch from 'hooks/useFetch'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMyHealth } from 'hooks/useMyHealth'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  API_DATE_FORMAT,
  assetImagePath,
  DISPLAY_DATE_FORMAT,
  LEADGEN_SUBMIT_KEY_DUEDATE,
  LOCALE_SPECS,
  SSO_URL,
} from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import {
  ArticleApi,
  CategoryApi,
  TogetherTopic,
  TogetherTopicApi,
} from 'interfaces/types'
import { TrimesterAddition } from 'interfaces/types/DueDate'
import { AppContext } from 'state/appContext'
import { useHealthToolStore } from 'state/healthTool'
import { useModalStore } from 'state/modal'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { sendCompleteFlowEvent } from 'utils/fpixel'
import { checkAndSaveUserInfo } from 'utils/healthTools'

import CurrentDueDateFaqs from './component/currentFaqs'
import DesktopNavResult, {
  DueDateNavResultTarget,
} from './component/desktopNavResult'
import DueDateDisclaimer from './component/disclaimer'
import DueDateIntroBanner from './component/dueDateIntroBanner'
import GuideAndSupport from './component/guideAndSupport'
import { LastestPregnancy } from './component/lastestPregnancy'
import { OtherAritlces } from './component/otherArticles'
import RecalculateBtnWithTooltip from './component/recalculateBtnWithTooltip'
import { RelatedTools } from './component/relatedTools'
import { ResultBanner } from './component/resultBanner'
import { SuggestedVideos } from './component/suggestedVideos'
import { TrimeterSlider } from './component/trimeterSlider'
import { WeekDrawer } from './component/weekDrawer'
import {
  StyledDueDate,
  StyledDueDateLeft,
  StyledDueDateResult,
  StyledDueDateRight,
} from './index.styled'
import { DueDateInfoType, Props, ResultStatus, Week } from './types'
import { addResultToLink } from '../helpers'
import { OnboardingFlow } from '../onboarding/onboardingFlow'

export type { DueDateInfoType, Props }

export const DueDatePageComponent = ({
  dueDatePageInfo,
  dueDateAllWeeks,
  query,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const isMobile = useIsMobile()
  const { locale, router, asPath } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const {
    isRecalculate,
    setIsRecalculate,
    setIsCalculating,
    setNavResultTarget,
  } = useDueDateStore()
  const [topics, setTopics] = useState<Array<TogetherTopic>>([])
  // const [isLeadGenFormOpen, setIsLeadGenFormOpen] = useState<boolean>(false)
  const redirectLink = useRef<string>('')
  const dueDateParam = useRef<DueDateInfoType | null>(null)
  const [previousData, setPreviousData] = useState<any>()

  const { formatMessage: f } = useIntl()
  const _type = query[QUERY_HEALTH_TOOL.type]
  const _startDate = query[QUERY_HEALTH_TOOL.startDate]
  const _additional = query[QUERY_HEALTH_TOOL.additional]
  const [isResultPageOnly, setIsResultPageOnly] = useState(
    typeof _type === 'string' &&
      _type.length > 0 &&
      typeof _startDate === 'string' &&
      _startDate.length > 0 &&
      typeof _additional === 'string' &&
      _additional.length >= 0,
  )
  const [resultStatus, setResultStatus] = useState(
    isResultPageOnly ? ResultStatus.loading : ResultStatus.idle,
  )

  const {
    state: { userInfo, isLoggedIn },
  } = useContext(AppContext)

  const [allTrimesters, setAllTrimesters] = useState<Array<Array<Week>>>([
    [],
    [],
    [],
  ])

  const { setDueDateInfo, setResultDueDate } = useHealthToolStore(
    (state) => state,
    shallow,
  )

  useEffect(() => {
    setDueDateInfo(dueDatePageInfo as any)
  }, [dueDatePageInfo])
  const [hasResult, setHasResult] = useState(false)
  const [inWhichTrimester, setInWhichTrimester] = useState(0) // the trimester that current week is in
  const [dueDate, setDueDate] = useState<Date>()
  const [startDate, setStartDate] = useState<Date>(() => new Date())
  const [currentWeek, setCurrentWeek] = useState<number>(0)
  const [selectedWeek, setSelectedWeek] = useState<number>(currentWeek)

  useEffect(() => {
    const trimesterLengths = allTrimesters.map((items) => items.length)
    let sum = 0
    for (let i = 0; i < trimesterLengths.length; i += 1) {
      sum += trimesterLengths[i]
      if (currentWeek < sum) {
        setInWhichTrimester(i)
        break
      } else if (i === trimesterLengths.length - 1) {
        setInWhichTrimester(i + 1)
      }
    }
  }, [currentWeek])

  const setShouldOnboardingModalOpen = useModalStore(
    (state) => state.setShouldOnboardingModalOpen,
  )

  useEffect(() => {
    setShouldOnboardingModalOpen(Boolean(hasResult))
  }, [dueDatePageInfo.is_leadgen, hasResult])

  const { response: suggestedArticlesRes } = useFetch({
    url: getApiPath(PATHS.HEALTH_TOOL.GET_DUE_DATE_SUGGESTED_ARTICLES, {
      from: `${currentWeek}`,
      to: `${currentWeek}`,
    }),
    method: 'GET',
    trigger: [hasResult, dueDate?.toString()],
    condition: hasResult,
  })
  const { data: suggestedArticlesDataRes } = suggestedArticlesRes || {}
  const { data: suggestedArticlesData } = suggestedArticlesDataRes || {}
  const { helpful_tips, results, embedding } = suggestedArticlesData || {}
  const { related_tool, suggested_videos } = helpful_tips || {}

  const suggestedArticles: ArticleApi[] = results?.[0]?.suggested_articles || []

  const pregnancyNavigationCategories: CategoryApi[] =
    embedding?.pregnacy_navigation

  const currentTrimesster = useMemo(
    () =>
      (dueDatePageInfo.trimesters?.[inWhichTrimester + 1] ||
        {}) as TrimesterAddition,
    [inWhichTrimester, dueDatePageInfo.trimesters],
  )

  const {
    title: relatedToolsTitle,
    health_tools: relatedTools,
  } = useMemo(() => {
    return {
      title:
        related_tool?.title ||
        f({ id: 'tool.relatedTools', defaultMessage: 'Suggested articles' }),
      health_tools: related_tool?.health_tools || [],
    }
  }, [related_tool?.title, related_tool?.health_tools])

  const {
    title: lastestPregnancyTitle,
    description: lastestPregnancyDescription,
    categories: lastestPregnancy,
  } = useMemo(() => {
    if (!currentTrimesster) {
      return {} as TrimesterAddition['categories']
    }
    return (currentTrimesster.categories ||
      {}) as TrimesterAddition['categories']
  }, [currentTrimesster])

  const {
    title: otherArticlesTitle,
    description: otherArticlesDescription,
    articles: otherArticles,
  } = useMemo(() => {
    if (!currentTrimesster) {
      return {} as TrimesterAddition['articles']
    }
    return (currentTrimesster?.articles || {}) as TrimesterAddition['articles']
  }, [currentTrimesster])

  const {
    title: suggestedVideosTitle,
    videos: suggestedVideos,
  } = useMemo(() => {
    return {
      title:
        suggested_videos?.title ||
        f({ id: 'common.suggestedVideos', defaultMessage: 'Suggested videos' }),
      videos: (suggested_videos?.video ?? []).map(formatVideoItem),
    }
  }, [suggested_videos?.title, suggested_videos?.video])

  const hasHelpfulTips =
    !!relatedTools.length || !!suggestedVideos.length || !!otherArticles?.length

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, [
      'health-tools/due-date',
    ]),
  })

  const { reloadMyHealthData } = useMyHealth({ initLoad: false })

  const handleLeadGen = (_redirectLink?: string) => {
    redirectLink.current = addResultToLink(_redirectLink || '')
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(redirectLink.current, 'due-date')
      return
    } else {
      router.push(redirectLink.current)
    }
    return true
  }

  const onSubmitDueDate = async ({
    startDate,
    dueDate,
    isFirstPregnant,
    otherInfo,
  }: DueDateInfoType) => {
    if (!startDate || !dueDate) {
      setIsCalculating(false)
      setResultStatus(ResultStatus.error)
      return
    }

    const localData = JSON.parse(localStorage.getItem(STORE_KEY) || '{}')
    // const isExisLocalData = Object.keys(localData).length > 0
    setPreviousData(localData)
    setResultDueDate(dueDate.getTime())
    setDueDate(dueDate)
    setStartDate(startDate)

    // includes today
    const dayDiffer = dayjs().diff(dayjs(startDate), 'day') + 1

    setCurrentWeek(new Date() > startDate ? Math.floor(dayDiffer / 7) + 1 : 1)
    setSelectedWeek(new Date() > startDate ? Math.floor(dayDiffer / 7) + 1 : 1)
    let allTrimestersTemp: Array<Array<Week>> = [[], [], []]
    let incrementalStartDate = startDate
    let index = 0
    if (dueDateAllWeeks) {
      while (dayjs(incrementalStartDate).isBefore(dueDate)) {
        const trimesterIndex =
          Math.floor((index + 1) / 13.5) > 2
            ? 2
            : Math.floor((index + 1) / 13.5)
        allTrimestersTemp[trimesterIndex].push({
          date: dayjs(incrementalStartDate).format(
            locale === 'zh-TW' ? 'MM/DD' : 'DD/MM',
          ),
          dateType2: dayjs(incrementalStartDate).format(
            locale === 'zh-TW' ? 'MM/DD' : 'DD/MM',
          ),
          dateType3: dayjs(incrementalStartDate).format(API_DATE_FORMAT),
          ...dueDateAllWeeks[index],
        } as Week)
        incrementalStartDate = dayjs(incrementalStartDate)
          .add(7, 'day')
          .toDate()
        index += 1
      }
    } else {
      allTrimestersTemp = []
    }
    setAllTrimesters(allTrimestersTemp)
    if (
      dueDatePageInfo.is_leadgen &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(LEADGEN_SUBMIT_KEY_DUEDATE) !== 'done'
    ) {
      // setIsLeadGenFormOpen(true)
      // redirectLink.current = _redirectLink || ''
      dueDateParam.current = { startDate, dueDate, otherInfo }
    }
    const parsedCookie = cookieParser.parse(document.cookie || '')
    await Promise.all([
      callApiNew(
        PATHS.SIDIS.POST_SIDIS_DUE_DATE,
        {
          method: 'POST',
          data: {
            account_id: userInfo?.id ?? null,
            site_id: parseInt(LOCALE_SPECS[locale].SITE_ID),
            ga_client_id: parsedCookie['_ga'] || '',
            email: userInfo?.email ?? null,
            user_name: userInfo?.name || userInfo?.username || '',
            baby_dob: null,
            ...otherInfo,
            trimesters: flatten(allTrimestersTemp).map((w, i) => ({
              week_number: i + 1,
              start_date: w.dateType3,
            })),
            position_type: 'landing_page',
            device_type: 'web ',
            url: window.location.href,
            categories: [],
            is_my_self: localData.isMyself,
            is_first_pregnant: isFirstPregnant,
            estimated_due_date: dayjs(dueDate).format(API_DATE_FORMAT),
            ...(localData.birthday && {
              birthday: localData.birthday,
            }),
            ...((localData.additionalQuestions || []).length > 0 && {
              additional_questions: localData.additionalQuestions,
            }),
            source_url: localData.source_url,
          },
        },
        'sidis_update',
      ),
      userInfo?.id &&
        localData?.isMyself &&
        localData?.birthday &&
        checkAndSaveUserInfo({
          userInfo,
          data: {
            birthday: dayjs(localData.birthday, DISPLAY_DATE_FORMAT).toDate(),
          },
          forceSaveBirthday: !localData?.isSubmitted,
        }),
    ])

    reloadMyHealthData()
    localStorage.removeItem(STORE_KEY)
    setHasResult(true)
    setResultStatus(ResultStatus.idle)
    setIsCalculating(false)
    setIsRecalculate(false)
  }

  const updateSidisData = (babyBirthday: Date) => {
    const parsedCookie = cookieParser.parse(document.cookie || '')
    callApiNew(
      PATHS.SIDIS.POST_SIDIS_DUE_DATE,
      {
        method: 'POST',
        data: {
          account_id: userInfo?.id ?? null,
          site_id: parseInt(LOCALE_SPECS[locale].SITE_ID),
          ga_client_id: parsedCookie['_ga'] || '',
          email: userInfo?.email ?? null,
          user_name: userInfo?.name || userInfo?.username || '',
          baby_dob: dayjs(babyBirthday).format(API_DATE_FORMAT),
          position_type: 'landing_page',
          device_type: 'web ',
          url: window.location.href,
        },
      },
      'sidis_update',
    )
  }

  const currentFaqs = useMemo(() => {
    if (!hasResult) {
      return (dueDatePageInfo?.informations || [])?.map((i) => ({
        title: i.title,
        content: i.description,
      }))
    }
    return currentTrimesster?.faqs || []
  }, [currentTrimesster?.faqs, hasResult, dueDatePageInfo?.informations])

  useEffect(() => {
    ;(async () => {
      try {
        if (hasResult && isLoggedIn) {
          const response = await callApiNew(
            PATHS.TOGETHER.GET_TOGETHER_TOPICS,
            {
              params: {
                dueDateMonth: dayjs(dueDate).get('month') + 1,
                dueDateYear: dayjs(dueDate).get('y'),
                showHidden: 1,
              },
            },
            'together',
          )

          const totalTopic = response?.data?.topics?.map(
            (topic: TogetherTopicApi) => formatTogetherTopic(topic),
          )
          setTopics(totalTopic)
        }
      } catch (error) {}
    })()
  }, [hasResult, dueDate])

  useEffect(() => {
    if (typeof userInfo === 'undefined') return
    ;(async () => {
      sendCompleteFlowEvent()

      if (
        typeof _type === 'string' &&
        _type.length > 0 &&
        typeof _startDate === 'string' &&
        _startDate.length > 0 &&
        typeof _additional === 'string' &&
        _additional.length >= 0
      ) {
        let calculateFn
        switch (Number(_type)) {
          case DueDateCalculatorType.lastPeriod:
            calculateFn = getDueDateLastPeriod
            break
          case DueDateCalculatorType.conception:
            calculateFn = getDueDateConception
            break
          case DueDateCalculatorType.IVF:
            calculateFn = getDueDateIVF
            break
          case DueDateCalculatorType.IUI:
            calculateFn = getDueDateIUI
            break
          case DueDateCalculatorType.ultrasound:
            calculateFn = getDueDateUltrasound
            break
          default:
            calculateFn = getDueDateLastPeriod
        }

        setResultStatus(ResultStatus.loading)
        const info = await calculateFn(new Date(_startDate), _additional)
        onSubmitDueDate(info)
        setIsResultPageOnly(false)
      }
    })()
  }, [typeof userInfo === 'undefined'])

  const articleLink = useMemo(
    () => dueDateAllWeeks?.[currentWeek - 1]?.recommended_article?.permalink,
    [dueDateAllWeeks, currentWeek],
  )
  const shareUrl = useMemo(() => {
    return queryString.stringifyUrl({
      url: `${getHelloSitesUrl(locale)}${getHealthToolLink(
        HEALTH_TOOL_TYPE.DUE_DATE,
      )}`,
      query: {
        result: 1,
        [QUERY_HEALTH_TOOL.startDate]: dayjs(dueDate).format('YYYY-MM-DD'),
        [QUERY_HEALTH_TOOL.type]: _type,
        [QUERY_HEALTH_TOOL.additional]: _additional,
      },
    })
  }, [dueDate, getHealthToolLink, hasResult])

  const [isOpenDrawer, setIsOpenDrawer] = useState(false)
  const [
    isOpenPregnancyNavigationDrawer,
    setIsOpenPregnancyNavigationDrawer,
  ] = useState(false)

  const _source = query[QUERY_HEALTH_TOOL.source]
  const userProfileUrl = `${SSO_URL}user`

  const pageItem = hasResult
    ? _source === 'my-health'
      ? [
          {
            label: f({
              id: 'healthTool.breadcrumb.myHealth',
              defaultMessage: 'My Health',
            }),
            value: `${userProfileUrl}/health`,
          },
          {
            label: f({
              id: 'dueDate.breadcrumb.result',
              defaultMessage: 'Due Date Result',
            }),
            value: '',
          },
        ]
      : [
          {
            label: f({ id: 'healthtools.title' }),
            value: `/health-tools`,
          },
          {
            label: f({ id: 'healthTools.dueDateCalculator' }),
            value: `${getHealthToolLink(HEALTH_TOOL_TYPE.DUE_DATE)}`,
          },
          {
            label: f({
              id: 'dueDate.breadcrumb.result',
              defaultMessage: 'Due Date Result',
            }),
            value: '',
          },
        ]
    : [
        {
          label: f({ id: 'healthtools.title' }),
          value: `/health-tools`,
        },
        {
          label: f({ id: 'healthTools.dueDateCalculator' }),
          value: `${getHealthToolLink(HEALTH_TOOL_TYPE.DUE_DATE)}`,
        },
      ]

  const dueDateHeading =
    dueDatePageInfo.title || f({ id: 'healthTools.dueDateCalculator' })
  const dueDateReviewBy = {
    avatar: dueDatePageInfo.reviewed_by?.avatar_thumbnail || '',
    reviewer: dueDatePageInfo.reviewed_by?.display_name || '',
    date: dueDatePageInfo.reviewed_date || '',
    link: formatUserProfileUrl(dueDatePageInfo?.reviewed_by),
  }

  useEffect(() => {
    const onScroll = () => {
      const sections = [
        document.getElementById(DueDateNavResultTarget.due_date_revealed),
        document.getElementById(DueDateNavResultTarget.guide_and_support),
        document.getElementById(DueDateNavResultTarget.helpful_tips_for_mom),
        document.getElementById(DueDateNavResultTarget.faq),
      ].filter(Boolean) as HTMLDivElement[]

      if (!sections.length) return

      const found = sections
        .map((section) => {
          const rect = section.getBoundingClientRect()
          const windowHeight =
            window.innerHeight || document.documentElement.clientHeight
          const windowWidth =
            window.innerWidth || document.documentElement.clientWidth

          if (
            rect.bottom <= 0 ||
            rect.top >= windowHeight ||
            rect.right <= 0 ||
            rect.left >= windowWidth
          ) {
            return {
              section,
              visibleHeight: 0,
            }
          }

          const visibleHeight =
            Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0)

          return { section, visibleHeight }
        })
        .reduce((result, item) => {
          return result.visibleHeight > item.visibleHeight ? result : item
        })

      if (found?.visibleHeight) {
        setNavResultTarget(found.section.id)
      }
    }

    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [isMobile])

  const hasFaqsSection = Boolean(currentFaqs?.length)

  return (
    <NavigatorLayout
      description={f({ id: 'dueDatePage.seoDescription' })}
      SEOData={{
        schemaData: f({ id: 'dueDatePage.seoSchema' }),
        isIndexable: true,
        apiMetaData: dueDatePageInfo?.meta_tag,
      }}
      footerInfo={footerInfo}
      isSponsored={dueDatePageInfo?.is_sponsored}
      sponsoredBy={dueDatePageInfo?.sponsor}
      sponsoredType="content"
      dataNavMenuContent={dataNavMenuContent}
      activeMenuService="tools"
      hideWelcomePopup={Boolean(dueDatePageInfo.is_leadgen && hasResult)}
    >
      {hasResult && (
        <OnboardingFlow
          campaign={dueDatePageInfo.collectingData.campaigns}
          isSkippable={dueDatePageInfo.is_skippable}
          phoneVerify={dueDatePageInfo.phone_verification}
          typeForm={dueDatePageInfo?.collectingTypes}
          toolName="duedate"
          extraPayloadValues={
            previousData
              ? {
                  childDoB: {
                    value: dayjs(previousData.dueDate).format(API_DATE_FORMAT),
                    isDate: true,
                  },
                }
              : undefined
          }
        />
      )}
      <StyledDueDate>
        {resultStatus === ResultStatus.loading && (
          <LoadingOverlay overlayBlur={2} visible={true} />
        )}
        {hasResult && isMobile && !isRecalculate && (
          <DesktopNavResult
            suggestedArticles={suggestedArticles}
            topics={topics || []}
            isShowFaqs={hasFaqsSection}
            sponsorBanner={dueDatePageInfo.sponsor_banner}
            hasHelpfulTips={hasHelpfulTips}
          />
        )}
        {(!hasResult || isRecalculate) && !isResultPageOnly && isMobile && (
          <Box
            sx={{
              overflow: 'hidden',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <ToolsBannerCardV2
              heading={dueDateHeading}
              description={
                dueDatePageInfo.description ||
                f({ id: 'dueDatePage.description' })
              }
              reviewBy={dueDateReviewBy}
              isMobile={isMobile}
              isPink
            />
          </Box>
        )}
        <StyledDueDateLeft data-result={String(hasResult)}>
          {!isMobile && (
            <Box mx={-16}>
              <HealthToolBreadcrumb pageItem={pageItem} />
            </Box>
          )}
          <div style={{ position: 'sticky', top: 112 }}>
            {!isMobile && (
              <>
                <Text size="h3" as={!hasResult ? 'h1' : 'h2'} mb={8} mt={40}>
                  {dueDateHeading}
                </Text>
                <ToolsBannerCardV2.MedicalReview
                  size="small"
                  isMobile={isMobile}
                  reviewBy={dueDateReviewBy}
                />
                <Space h={24} />
              </>
            )}
            {(!hasResult || isRecalculate) && (
              <>
                <DueDateCalculator
                  onSubmitDueDate={(info, redirectLink) => {
                    const checkLeadGen = handleLeadGen(redirectLink)
                    if (checkLeadGen) {
                      onSubmitDueDate(info)
                    }
                  }}
                  additionalQuestions={dueDatePageInfo?.listQuestions || []}
                  controlledSubmit={
                    dueDatePageInfo.is_leadgen &&
                    typeof localStorage !== 'undefined' &&
                    localStorage.getItem(LEADGEN_SUBMIT_KEY_DUEDATE) !== 'done'
                  }
                  adUnitPath={isMobile ? adUnitPath : ''}
                />
              </>
            )}
            {hasResult && !isMobile && !isRecalculate && (
              <>
                <DesktopNavResult
                  suggestedArticles={suggestedArticles}
                  topics={topics || []}
                  isShowFaqs={hasFaqsSection}
                  sponsorBanner={dueDatePageInfo?.sponsor_banner}
                  hasHelpfulTips={hasHelpfulTips}
                />
                <Box pb={16}>
                  <DueDateDisclaimer
                    description={dueDatePageInfo.disclaimer}
                    bordered={false}
                  />
                </Box>
              </>
            )}
          </div>
        </StyledDueDateLeft>
        {resultStatus !== ResultStatus.error ? (
          <StyledDueDateRight style={hasResult ? { padding: 0 } : {}}>
            {hasResult && !isMobile && (
              <TrimeterSlider
                currentWeek={currentWeek}
                isSponsored={dueDatePageInfo?.is_sponsored}
              />
            )}
            {hasResult && (
              <StyledDueDateResult>
                {!isMobile && <RecalculateBtnWithTooltip />}
                <div>
                  <Box
                    px={isMobile ? 16 : 0}
                    pb={isMobile ? 16 : 0}
                    bg={theme.colors.pink50}
                    id={DueDateNavResultTarget.due_date_revealed}
                  >
                    <ResultBanner
                      dueDate={dueDate}
                      currentWeek={currentWeek}
                      articleLink={articleLink}
                    />
                    <StyledDueDateWrapper>
                      <DueDateCalculatorCard
                        onClickGoToOvulationTool={(babyBirthday?: Date) => {
                          if (babyBirthday) {
                            updateSidisData(babyBirthday)
                          }
                          router.push(
                            getHealthToolLink(HEALTH_TOOL_TYPE.OVULATION) ||
                              '/',
                          )
                        }}
                        dueDateWeeks={dueDateAllWeeks}
                        currentWeek={currentWeek}
                        selectedWeek={selectedWeek}
                        firstDay={startDate}
                        onClickWeek={() => setIsOpenDrawer(true)}
                      />
                    </StyledDueDateWrapper>
                    {!isMobile && (
                      <Flex justify="center" mb={32}>
                        <SharePopupNew.BottomNavHorizonal
                          shareUrl={shareUrl}
                          showFull
                          iconSize={24}
                          styles={{
                            heading: {
                              fontSize: 12,
                            },
                          }}
                          bgColor={theme.colors.white}
                          bgColorCopyBtn={theme.colors.gray100}
                        />
                      </Flex>
                    )}
                  </Box>

                  {isMobile && (
                    <Box px={16} py={24}>
                      <DueDateDisclaimer
                        description={dueDatePageInfo.disclaimer}
                        bordered={false}
                      />
                    </Box>
                  )}
                </div>
                <GuideAndSupport
                  titleArticle={
                    dueDateAllWeeks[selectedWeek - 1]?.title_article
                  }
                  descriptionArticle={
                    dueDateAllWeeks[selectedWeek - 1]?.description_article
                  }
                  suggestedArticles={suggestedArticles}
                  sponsorBanner={dueDatePageInfo.sponsor_banner}
                  topics={topics}
                  dueDate={dueDate}
                  adUnitPath={adUnitPath}
                />

                {/* Helpful tips for mom */}
                {hasHelpfulTips && (
                  <div
                    id={DueDateNavResultTarget.helpful_tips_for_mom}
                    className="mt-0 md:mt-16"
                  >
                    <RelatedTools
                      title={relatedToolsTitle}
                      tools={relatedTools}
                    />
                    <LastestPregnancy
                      title={lastestPregnancyTitle}
                      description={lastestPregnancyDescription}
                      categories={lastestPregnancy}
                      className="mt-0 md:mt-10"
                    />
                    <OtherAritlces
                      title={otherArticlesTitle}
                      description={otherArticlesDescription}
                      articles={otherArticles}
                      className="mt-0 md:mt-10"
                    />
                    <SuggestedVideos
                      title={suggestedVideosTitle}
                      videos={suggestedVideos}
                      className="mt-0 md:mt-10"
                    />
                  </div>
                )}

                {!isMobile && hasFaqsSection && (
                  <Box id={DueDateNavResultTarget.faq} pt={64} mih="80vh">
                    <CurrentDueDateFaqs
                      currentFaqs={currentFaqs}
                      bgContentColor={theme.colors.white}
                    />
                  </Box>
                )}
              </StyledDueDateResult>
            )}

            {!hasResult && (
              <StyledDueDateResult>
                {!isMobile && (
                  <DueDateIntroBanner
                    featuredImage={dueDatePageInfo?.featured_image}
                  />
                )}
                <DueDateDisclaimer description={dueDatePageInfo.disclaimer} />
                {/* <DueDateInfo dueDateInfos={dueDatePageInfo?.informations} /> */}
                <CurrentDueDateFaqs currentFaqs={currentFaqs} />
              </StyledDueDateResult>
            )}

            <div className="bottom-ad"></div>
          </StyledDueDateRight>
        ) : (
          <StyledDueDateRight data-error="true">
            <Flex maw={400} py={16} gap={16} direction="column" align="center">
              <img
                src={`${assetImagePath}/due-date-calculation-error.png`}
                style={{
                  width: '200px',
                  height: '200px',
                  objectFit: 'contain',
                  maxWidth: '100%',
                }}
                alt=""
                loading="lazy"
              />
              <Text size="h3" align="center" color={theme.colors.gray800}>
                Oops
              </Text>
              <Text size="p3" align="center" color={theme.colors.gray700}>
                {f({ id: 'dueDatePage.error.description' })}
              </Text>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => router.push('/health-tools')}
              >
                {f({
                  id: 'dueDatePage.explore_other_tools',
                  defaultMessage: 'Explore other tools',
                })}
              </Button>
            </Flex>
          </StyledDueDateRight>
        )}
      </StyledDueDate>
      {isMobile && hasFaqsSection && (
        <Container size="lg" id={DueDateNavResultTarget.faq}>
          <CurrentDueDateFaqs currentFaqs={currentFaqs} />
        </Container>
      )}
      {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}
      <MobileBottomNavigationWrapper.FitnessHealthTool
        appTool={!hasResult ? 'baby-kick-counter' : undefined}
        isDueDateRecalculate={hasResult}
        shareUrl={shareUrl}
      />
      {isMobile && (
        <>
          <WeekDrawer
            open={isOpenDrawer}
            onClose={() => setIsOpenDrawer(false)}
            dueDateAllWeeks={dueDateAllWeeks}
            firstDay={startDate}
            currentWeek={selectedWeek}
            onWeekClick={(value) => setSelectedWeek(Number(value))}
          />

          <PregnancyNavigation
            categories={
              hasResult
                ? pregnancyNavigationCategories
                : (dueDatePageInfo.pregnacy_navigation as any)
            }
            drawerProps={{
              open: isOpenPregnancyNavigationDrawer,
              onClose: () => setIsOpenPregnancyNavigationDrawer(false),
            }}
          />
        </>
      )}
      {hasResult && (
        <AdBlockContainer>
          <FixedBottomAdBlock
            adUnitPath={adUnitPath}
            className="article-bottom-ad"
            isMobile={isMobile}
          />
          <OutOfPageAd id={`div-gpt-out-of-page`} adUnitPath={adUnitPath} />
        </AdBlockContainer>
      )}
    </NavigatorLayout>
  )
}
